type RemoveUndefined<T> = {
  [K in keyof T as undefined extends T[K] ? never : K]: T[K];
};

/**
 * Removes undefined values from an object.
 * NOTE: This only remove the top level undefined values.
 *
 * @example
 * const foo = {
 *   a: 1,
 *   b: undefined,
 *   c: 3,
 *   d: null,
 *   e: {
 *     f: undefined,
 *     g: 4,
 *   },
 * };
 *
 * // type of bar is { a: number, c: number, d: null, e: { f: undefined, g: number } }
 * const bar = removeUndefined(foo);
 *
 * // Output: { a: 1, c: 3, d: null, e: { f: undefined, g: 4 } }
 * console.log(bar);
 *
 * @param obj - The object to remove undefined values from
 * @returns The object with undefined values removed
 */
export const removeUndefined = <T extends Record<string, unknown>>(obj: T): RemoveUndefined<T> => {
  const entries = Object.entries(obj).filter(([_, v]) => v !== undefined);

  return Object.fromEntries(entries) as RemoveUndefined<T>;
};
